import { NavLink } from 'react-router';
import type { ReactNode } from 'react';

// `?no-inline`: a file of its own rather than base64 inside the one JavaScript
// chunk, which scripts/check-bundle.mjs holds to a ceiling.
import logoOnDark from '../../assets/vy-life-logo-light.png?no-inline';
import { ACTIONS, APP_NAME } from '../../copy';
import { AREA_LABEL, signOut, useRequiredSession, type Area } from '../../session';
import { NAVIGATION, filterNavigation } from './navigation';
import { SimulatedClock } from './SimulatedClock';

/**
 * The frame every signed-in screen renders inside.
 *
 * One layout, two navigations. Which one appears is decided by the area the
 * signed-in principal belongs to, so a screen never has to think about it, and
 * the customer portal and the back-office cannot drift into looking like two
 * unrelated products.
 *
 * The skip link and the landmark roles are here rather than per page because
 * they are the kind of thing that is added once and forgotten forever, and the
 * people doing HR data entry on these screens do it all day.
 *
 * The shell is the vy life one (#406): a plum sidebar holding the brand, the
 * navigation and who is signed in, and a white top bar over the page holding
 * where you are -- the area and the company -- the simulated clock and the
 * company switcher. The top bar is the `banner` landmark, so everything that
 * says which company a screen is about stays in one place.
 *
 * The simulated clock sits in the same top bar for both areas, and renders
 * nothing where the deployment has no clock to move -- which is every
 * production deployment. See {@link SimulatedClock}.
 *
 * The customer portal fills two slots (#392): `accountDetail`, the company the
 * portal is showing -- which for a person registered at a company group is the
 * one they chose, not one they are registered at -- and `toolbar`, where the
 * company switcher sits so that it is on every customer page.
 */
export function AppLayout({
  area,
  children,
  accountDetail,
  toolbar,
}: {
  area: Area;
  children: ReactNode;
  accountDetail?: ReactNode;
  toolbar?: ReactNode;
}) {
  const session = useRequiredSession();
  // Filtered rather than rendered whole: a link to a screen this principal
  // would be refused on is a link to a 403, and an analyst who clicks one
  // learns to distrust the whole sidebar.
  const items = filterNavigation(NAVIGATION[area], session.principal.permissions);
  const company = session.principal.company;

  return (
    <div className="layout">
      <a className="skip-link" href="#conteudo">
        Ir para o conteúdo
      </a>

      <aside className="layout__sidebar">
        <div className="layout__brand">
          <img className="layout__logo" src={logoOnDark} alt={APP_NAME} width={108} height={36} />
          <span className="layout__tagline">uma plataforma vyttech</span>
        </div>

        <nav className="layout__nav" aria-label="Navegação principal">
          <ul>
            {items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end ?? false}
                  className={({ isActive }) =>
                    isActive ? 'layout__nav-link layout__nav-link--active' : 'layout__nav-link'
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="layout__user">
          <span className="layout__account-name">{session.principal.display_name}</span>
          <button type="button" className="button button--ghost" onClick={signOut}>
            {ACTIONS.signOut}
          </button>
        </div>
      </aside>

      <div className="layout__column">
        <header className="layout__header">
          <div className="layout__context">
            <span className="layout__area">{AREA_LABEL[area]}</span>
            <span className="layout__account-detail">
              {accountDetail ??
                (company !== null && company !== undefined ? company.name : 'vyttech')}
            </span>
          </div>

          <SimulatedClock permissions={session.principal.permissions} />

          {toolbar}
        </header>

        <main className="layout__main" id="conteudo">
          {children}
        </main>
      </div>
    </div>
  );
}
